import { useEffect, useRef, useState } from 'react';

import { formatTtdFromPoints as formatPanelistTtdFromPoints } from '@trinidad/shared/incentives';

type ProductStatsBandProps = {
  className?: string;
};

const snackOptions = ['Doubles', 'Pholourie', 'Bake & shark', 'Pastelle'] as const;

const walletReceipts = [
  {
    kicker: 'Airtime sent',
    line: 'TT$34 · Digicel · Ref DC-58102',
    tone: 'ok',
  },
  {
    kicker: 'Study completed',
    line: '+TT$9.09 · Grocery habits · 14 min',
    tone: 'neutral',
  },
] as const;

const productStats = [
  {
    line: 'is all you need to cash out',
    stat: 'TT$25.00',
  },
  {
    line: 'typical study length',
    stat: '5-45 min',
  },
  {
    line: 'sent to Digicel or bMobile numbers',
    stat: 'TT$ airtime',
  },
];

export function ProductUiComposition({ className = '' }: { className?: string }) {
  const walletRef = useRef<HTMLElement | null>(null);
  const [balance, setBalance] = useState(() => (typeof window === 'undefined' ? 2450 : 0));

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setBalance(2450);
      return;
    }

    let hasRun = false;
    let animationFrame = 0;

    function runCountUp() {
      if (hasRun) {
        return;
      }

      hasRun = true;
      const startedAt = performance.now();
      const durationMs = 280;

      function tick(now: number) {
        const progress = Math.min((now - startedAt) / durationMs, 1);
        const eased = 1 - (1 - progress) ** 3;

        setBalance(Math.round(2450 * eased));

        if (progress < 1) {
          animationFrame = window.requestAnimationFrame(tick);
        }
      }

      animationFrame = window.requestAnimationFrame(tick);
    }

    const wallet = walletRef.current;

    if (!wallet || !('IntersectionObserver' in window)) {
      runCountUp();
      return () => window.cancelAnimationFrame(animationFrame);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          runCountUp();
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );

    observer.observe(wallet);

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <div className={`product-ui-composition product-phone-composition ${className}`.trim()}>
      <article className="hero-phone-frame hero-phone-frame-survey" data-hero-phone="survey">
        <div aria-hidden="true" className="hero-phone-notch" />
        <div className="hero-phone-micro">5-10 min study · TT$5.00</div>
        <div className="hero-phone-sub">Question 3 of 5</div>
        <h2 className="hero-phone-question">Which local snack do you reach for most?</h2>
        <div className="hero-phone-options">
          {snackOptions.map((option) => (
            <div
              className={`hero-phone-option ${option === 'Pholourie' ? 'is-selected' : ''}`.trim()}
              key={option}
            >
              {option === 'Pholourie' ? (
                <span
                  aria-hidden="true"
                  className="bullseye-marker bullseye-marker-action hero-phone-option-marker"
                />
              ) : null}
              {option}
            </div>
          ))}
        </div>
        <div className="hero-phone-button hero-phone-button-red">Next</div>
      </article>

      <article
        className="hero-phone-frame hero-phone-frame-wallet"
        data-hero-phone="wallet"
        ref={walletRef}
      >
        <div aria-hidden="true" className="hero-phone-notch" />
        <div className="hero-phone-micro">Your balance</div>
        <div className="hero-phone-stat">{formatPanelistTtdFromPoints(balance)}</div>
        <p className="hero-phone-line">Ready as prepaid airtime</p>
        <div className="hero-phone-receipts">
          {walletReceipts.map((receipt, index) => (
            <div
              className={`hero-phone-receipt hero-phone-receipt-${index + 1}`}
              key={receipt.kicker}
            >
              <div className={`hero-phone-receipt-kicker hero-phone-receipt-${receipt.tone}`}>
                {receipt.kicker}
              </div>
              <div className="hero-phone-receipt-line">{receipt.line}</div>
            </div>
          ))}
        </div>
        <div className="hero-phone-button hero-phone-button-ink">Cash out</div>
      </article>
    </div>
  );
}

export function ProductStatsBand({ className = '' }: ProductStatsBandProps) {
  return (
    <div className={`product-stats-band ${className}`.trim()}>
      {productStats.map((item) => (
        <article className="product-stat-card" data-product-stat-card="true" key={item.stat}>
          <div className="product-stat-value" data-product-stat-value="true">
            {item.stat}
            <span aria-hidden="true" className="product-stat-stop">
              .
            </span>
          </div>
          <p>{item.line}</p>
        </article>
      ))}
    </div>
  );
}
